import { createDecorator } from "vue-class-component";
import { ComputedOptions } from "vue/types/options";
import Vue, { ComponentOptions } from "vue";
import ResizeObserver from "resize-observer-polyfill";
import { VueClass } from "vue-class-component/lib/declarations";
import { clearStore } from "@/lib/vuex";
import debounce from "lodash-es/debounce";
import throttle from "lodash-es/throttle";
import { WatchRoute as WatchRouteMixin } from "@/mixins/WatchRoute";
import { FormValues } from "@/types";
import { isEqual } from "lodash-es";

export * from "@/lib/helpers/componentViewController/decorators";

export const NoCache = createDecorator((options, key) => {
  if (options && options.computed && options.computed[key]) {
    (options.computed[key] as ComputedOptions<any>).cache = false;
  }
});

export interface TargetOptions {
  options?: {
    methods: { [k: string]: (...args: any[]) => any };
    mounted: Array<() => any> | undefined;
    beforeDestroy: Array<() => any> | undefined;
    mixins?: Array<ComponentOptions<Vue> | typeof Vue>;
  };
}

// Handler should be component method name that accepts new width as first argument
export const WatchParentResize = <V extends Vue>(handler: string) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    if (!Object.getOwnPropertyDescriptor(target, "options")) {
      throw new Error(
        "Can't access component options. Make sure that you used decorator above the @Component decorator"
      );
    }
    if (!target.options) return target;
    if (!target.options.methods || !target.options.methods[handler]) {
      throw new Error("Can't find handler function. Did you defined handler properly?");
    }
    const handlerFunc = target.options.methods[handler];
    let observer: ResizeObserver;
    if (!target.options.mounted) target.options.mounted = [];
    if (!target.options.beforeDestroy) target.options.beforeDestroy = [];

    function mounted(this: Vue) {
      observer = new ResizeObserver(mutations => {
        mutations.forEach(mutation => {
          handlerFunc.call(this, mutation.contentRect.width);
        });
      });
      if (this.$el.parentElement !== null) {
        observer.observe(this.$el.parentElement);
      } else {
        throw Error("Cant find parent element");
      }
    }

    function beforeDestroy(this: Vue) {
      observer.disconnect();
    }

    target.options.mounted.push(mounted);
    target.options.beforeDestroy.push(beforeDestroy);

    return target;
  };
};

export const ClearStoreOnDestroy = <V extends Vue>(storePath: string) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    return target.mixin(clearStore(storePath)) as VC & TargetOptions;
  };
};

export const Debounce = (time: number) =>
  createDecorator((options, key) => {
    if (options && options.methods && options.methods[key]) {
      const orig = options.methods[key];
      options.methods[key] = debounce(orig, time);
    }
  });

interface ThrottleSettings {
  leading?: boolean;
  trailing?: boolean;
}

export const Throttle = (time: number, opt?: ThrottleSettings) =>
  createDecorator((options, key) => {
    if (options && options.methods && options.methods[key]) {
      const orig = options.methods[key];
      options.methods[key] = throttle(orig, time, opt);
    }
  });

export const WatchRoute = <V extends Vue>(handler: string) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    return target.mixin(WatchRouteMixin(handler)) as VC & TargetOptions;
  };
};

export const StoreInQuery = (deleteValues: string[] = []) =>
  createDecorator((options, key) => {
    if (!options.methods) options.methods = {};
    type Method = <V extends Vue>(this: V, ...args: any[]) => any;

    let orig: null | Method = null;
    if (options && options.methods && options.methods[key]) {
      orig = options.methods[key];
    }
    options.methods[key] = function(...args: any[]) {
      let values = args[0] as FormValues;
      if (orig) values = orig.apply(this, args) as FormValues;

      const query = { ...this.$route.query };
      const valueKeys = Object.keys(values);
      valueKeys.forEach(k => {
        const value = values[k];
        if (query[k]) delete query[k];
        if (value === null || value === "") return;
        query[k] = value.toString();
      });

      if (!isEqual(this.$route.query, query)) {
        deleteValues.forEach(valueToDelete => delete query[valueToDelete]);
        this.$router.replace({ query });
      }
    };
  });
